import _ from 'lodash'
import * as d3 from 'd3-selection'
import * as d3drag from 'd3-drag'
import { NO_DRAG_CLASS } from 'scoreboard/reports/common/table/header-cell'
import constants from 'scoreboard/components/constants'
import reportConstants from 'scoreboard/reports/report-constants'
import $ from 'jquery'
import KeyCodes from 'scoreboard/components/key-codes'
import cloneDeepWithModels from 'scoreboard/components/clone-deep-with-models'
import Table, { DND_HEADER_CLASS } from './table'

function within(zone, x, y) {
	return zone && zone.x1 < x && x < zone.x2 && zone.y1 < y && y < zone.y2
}

/**
 * The size in pixel of the buffer added to boundary edges on the entire
 * drop zone (e.g. the extension of space beyond border of cells on the perimeter
 * of the drop zone)
 */
const DROP_ZONE_BUFFER_PX = 20

//dev property, set to true to enable debugging of the dnd
const debug = false

/**
 * Responsible for handling dragging of stacks in the table used by report
 * designer
 */
class TableDnd {

	constructor(table, opts = {}) {
		if (!table) { throw new Error('Table is required')}
		this.table = table

		let head = this.table.header()
		if (!head || _.isEmpty(head.stacks())) return this

		//setup the d3 drag infrastructure
		let drag = d3drag.drag().filter(function () {
			return !d3.select(this).classed(NO_DRAG_CLASS)
		})
		let events = {
			'start': this._onStart.bind(this),
			'drag': this._onMove.bind(this),
			'end': this._onEnd.bind(this)
		}
		let tableDnd = this
		_.each(events, (cb, type) => drag.on(type, function(evt, d, i) {
			tableDnd._currentPosition = d3.pointer(evt, document.body)
			cb(d, i, this, evt)
		}))

		//number of pixel before we detect a drag vs click
		this.clickDistanceThreshold =  opts.clickDistance || 15
		drag.clickDistance(this.clickDistanceThreshold)

		this.table.selection().selectAll('td.dnd-header').call(drag)
	}

	_setupZones() {
		if (debug) {
			$('.zoneDebug').remove()
		}

		// Initializes the dnd by calculating the drag zones for each stack in the
		// table.  These drag zones are used to determine how to interpret a drop
		let buffer = DROP_ZONE_BUFFER_PX
		this._tableDim = this.table.dimensions()
		this._zoneState = {
			zones: [],
			zonesById: {},
			bounds: null,
			currentZone: null,
			currentZoneAdjacents: null,
			adjTop: null,
			adjBottom: null,
			adjLeft: null,
			adjRight: null
		}
		this._zoneState.bounds = {
			left: -buffer,
			right: this._tableDim.width + buffer,
			top: -buffer,
			bottom: this._tableDim.height + buffer
		}
		let head = this.table.header()
		if (!head || _.isEmpty(head.stacks())) return this

		let headerZones = []
		_.each(head.cells(), cellRow => {
			let zone = []
			_.each(cellRow, headerCell => {
				_.each(headerCell.domNodes(), node => {
					if (node.classList.contains(DND_HEADER_CLASS)) {
						zone = zone.concat(this._computeZones(node, headerCell))
					}
				})
			})
			headerZones.push(zone)
		})
		this._zoneState.zones = headerZones
	}

	/**
	 * handler for d3's 'start' event, resets the tracking of accumulated
	 * distance during a drag which is used to delineate between a click and a
	 * drag
	 */
	_onStart(sourceColumnCell, idx, el) {
		delete this._dragging
		this.dragTimeout = setTimeout(this._onDragStart.bind(this),
			constants.DRAG_THRESHOLD_MS, sourceColumnCell)
	}

	_onDragStart(sourceCell) {
		let sourceColumnCell = this._getDndHeaderCell(sourceCell)

		//on the start of a drag
		this._dragging = true

		//https://github.com/SpiderStrategies/Scoreboard/issues/38255, esc cancels drag
		d3.select('body').on('keydown', (e) => {
			if(e.keyCode === KeyCodes.ESC) {
				this._stopDrag(sourceColumnCell, true)
				d3.select('body').on('keydown', null) // remove the listener
			}
		})

		//setup the zone on each start since the table might have repositioned
		//since last time
		this._setupZones()

		let nodeBeingDragged = sourceColumnCell.domNodes()[0]
		//generate an avatar whenever the stack signals to
		sourceColumnCell.tableStack.on('avatar', (stacks, opts = {}) => {
			this._createAvatar(stacks, this.table.data(), nodeBeingDragged, opts)
		})
		sourceColumnCell.dragStart(this._getZoneOfCurrentPosition())
		this.table.selection().classed('is-rearranging-stacks', true)
	}

	/**
	 * handler for d3's 'drag' event. Notify's external handlers of the actual drag
	 */
	_onMove(sourceCell, idx, el) {
		if (!this._dragging) {
			return	//do nothing if we're not dragging
		}
		let sourceColumnCell = this._getDndHeaderCell(sourceCell)

		//check the drag position, find the zone we're in if any
		let { currentZone, currentZoneAdjacents } = this._zoneState
		let newZone = this._getZoneOfCurrentPosition()

		//check the various changes in zones
		if (currentZone) {
			if (currentZone !== newZone) {
				currentZone.headerCell.dragExit(sourceColumnCell, currentZone,
					currentZoneAdjacents)
				if (newZone) {
					this._enterNewZone(sourceColumnCell, newZone)
				}
			}
		} else if (newZone) {
			this._enterNewZone(sourceColumnCell, newZone)
		}

		//update the avatar position if any
		if (this._avatar) {
			this._setAvatarUnderCurrentPosition()
		}
	}

	_enterNewZone(sourceColumnCell, newZone) {
		//update the current zone
		this._zoneState.currentZone = newZone
		this._zoneState.currentZoneAdjacents = this._getAdjacentZones(newZone)
		newZone.headerCell.dragEnter(sourceColumnCell, newZone,
			this._zoneState.currentZoneAdjacents)
	}

	/**
	 * handler for d3's 'end' event. Notify's external handlers to do
	 * the drop and clean up at the end of the drag
	 */
	_onEnd(sourceCell, idx, el) {
		let sourceColumnCell = this._getDndHeaderCell(sourceCell)
		clearTimeout(this.dragTimeout)
		if (!this._dragging) { return }
		this._stopDrag(sourceColumnCell)
	}

	_stopDrag(sourceColumnCell, cancel = false) {
		let zone = this._getZoneOfCurrentPosition()
		sourceColumnCell.dragEnd(zone, cancel)
		delete this._dragging
		this.table.selection()
			.classed('is-rearranging-stacks', false)
		this._destroyAvatar()

		//stop listener for avatar event
		sourceColumnCell.tableStack.removeAllListeners('avatar')
	}

	_getDndHeaderCell(sourceCell) {
		//if this cell defines a header cell reference then the drag came from
		//the column body, use the header cell reference as that contains all the
		//information to do the actual DND #40687
		return sourceCell.headerCell || sourceCell
	}

	_computeZones(domNode, headerCell) {
		let buffer = DROP_ZONE_BUFFER_PX
		let dim = domNode.getBoundingClientRect()

		//left and top zone is calculated relative from the corner of the table
		let leftEdge = dim.left - this._tableDim.x
		let topEdge = dim.top - this._tableDim.y

		let midXPoint = leftEdge + _.floor(dim.width/2)
		let rightEdge = leftEdge + dim.width
		let leftBuffer = leftEdge == 0 ? buffer : 0
		let rightBuffer = rightEdge == this._tableDim.width ? buffer : 0

		//if the header is not a multi column stack OR if we're the bottom row of a repeat by stack
		//then extend the zone to the bottom of the table
		let expandToTableBottom = _.isNil(headerCell.position) ||
			headerCell.position == reportConstants.REPEAT_BY_BOTTOM_POSITION
		let height = expandToTableBottom ? (this._tableDim.height-topEdge+buffer)
			: dim.height

		let midYPoint = topEdge + _.floor(dim.height/2)
		let bottomEdge = topEdge + height
		let topBuffer = topEdge == 0 ? buffer : 0

		//the +1 on the midPoints here are to eliminate the flickering shown here
		//https://slack-files.com/T0293R8M7-F025301PE0Y-8afb34fb53
		let left1 = leftEdge - leftBuffer
		let left2 = midXPoint + 1
		let right1 = midXPoint + 1
		let right2 = rightEdge + rightBuffer
		let top1 = topEdge - topBuffer
		let top2 = midYPoint + 1
		let bottom1 = midYPoint + 1
		let bottom2 = bottomEdge

		//each cell is divided into 2 sets of halves, left/right, top/bottom
		//for 4 zones per cell.  The values are relative to the parent container
		//of the dragged element
		let headerId = headerCell.id()
		let leftTopZone = {
			id: `${headerId}-left-top`,
			x1: left1, x2: left2, y1: top1, y2: top2,
			left: true, top: true,
			headerCell,
			position: 'left-top',
			leftEdge, topEdge, rightEdge, bottomEdge
		}
		let rightTopZone = {
			id: `${headerId}-right-top`,
			x1: right1, x2: right2, y1: top1, y2: top2,
			left: false, top: true,
			headerCell,
			position: 'right-top',
			leftEdge, topEdge, rightEdge, bottomEdge
		}
		let leftBottomZone = {
			id: `${headerId}-left-bottom`,
			x1: left1, x2: left2, y1: bottom1, y2: bottom2,
			left: true, top: false,
			headerCell,
			position: 'left-bottom',
			leftEdge, topEdge, rightEdge, bottomEdge
		}
		let rightBottomZone = {
			id: `${headerId}-right-bottom`,
			x1: right1, x2: right2, y1: bottom1, y2: bottom2,
			left: false, top: false,
			headerCell,
			position: 'right-bottom',
			leftEdge, topEdge, rightEdge, bottomEdge
		}

		let computedZones = [leftTopZone, rightTopZone, leftBottomZone, rightBottomZone]

		if (debug) {
			//when debugging, overlays the drop zones so we can visually see them
			const backgroundColor = '#' + Math.floor(Math.random() * Math.pow(2,24)).toString(16).padStart(6, '0')
			_.each(computedZones, z => {
				let top = z.y1 + this._tableDim.y
				let left = z.x1 + this._tableDim.x
				let width = z.x2 - z.x1
				let height = z.y2 - z.y1
				let debugNode = $(document.createElement('div'))
				debugNode.css({
					backgroundColor,
					opacity: .25,
					zIndex: 999,
					pointerEvents: 'none',
					position: 'absolute',
					height,
					width,
					top,
					left
				}).text(headerId).addClass('zoneDebug').insertAfter(document.body)
				console.log(`${z.id}: X1,2: ${z.x1} ${z.x2}  Y1,2: ${z.y1} ${z.y2}`)
			})
		}
		return computedZones
	}

	/**
	 * Takes the given position returned by the d3 event and returns the drag zone
	 * it's in, or null if it's not in one.
	 */
	_getZoneOfCurrentPosition() {
		//zones are calculated based off the table dimension, so we need to calculate
		//the current mouse position relative to the table itself
		let xRelFromTable = this._currentPosition[0] - this._tableDim.x
		let yRelFromTable = this._currentPosition[1] - this._tableDim.y

		let { bounds, currentZone } = this._zoneState
		//first check if we're out of bounds
		if (bounds.left > xRelFromTable || bounds.right < xRelFromTable ||
			bounds.top > yRelFromTable || bounds.bottom < yRelFromTable) {
			return null
		}

		//next check to see if we're still in the current zone
		if (within(currentZone, xRelFromTable, yRelFromTable)) {
			return currentZone
		}
		return this._findZone(xRelFromTable, yRelFromTable)
	}

	/**
	 * Given a zone, returns the above/below zones adjacent to this zone at the
	 * current cursor X position relative to the table's position
	 *
	 * @param zone The zone to find the adjacent zone
	 * @return {{below: *, above: *}}
	 * @private
	 */
	_getAdjacentZones(zone) {
		let xRelFromTable = this._currentPosition[0] - this._tableDim.x
		return  {
			above: this._findZone(xRelFromTable, zone.topEdge - 1),
			below: this._findZone(xRelFromTable, zone.bottomEdge + 1)
		}
	}

	/**
	 * Takes an x,y coordinate and find the zone it's in starting from the lowest
	 * subcolumn (i.e. the last row in the 2D column meta array) and working its
	 * way up to the highest level (i.e. the main columns in the table), or null
	 * if it's not in a drop zone
	 */
	_findZone(x,y) {
		let { zones } = this._zoneState
		let zone
		//start from the lowest subcolumn and work your way up
		for (let i=zones.length; --i > -1;) {
			zone = _.find(zones[i], zone => within(zone, x, y))
			if (zone) {
				return zone
			}
		}
		return null
	}

	_createAvatar(stacks, data, draggedDomNode, opts) {
		let avatarInDrag = this._dragging && !!this._avatar
		if (avatarInDrag && this._avatar.stacks === stacks) {
			return  //same avatar do not create new one
		}
		this._destroyAvatar()

		//create the movable table with the data from above
		this._avatar = d3.select(this.table.selection().node().parentNode)
			.append('table').classed('stack-avatar table', true)
			.classed(opts.dragType, opts.dragType)
		//keep a reference on the stack data to prevent unnecessary creation of the same avatar
		this._avatar.stacks = stacks

		//deep clone to ensure the avatar is completely separate from the table
		// #56490 important to use cloneDeepWithModels instead of
		// _.cloneDeep() or we end up with bad clones of Backbone Views
		let stackData = cloneDeepWithModels(stacks)

		let avatarNode = this._avatar.node()
		new Table([{header: stackData, data, groupIndex: 0}],
			avatarNode, {
				editable: false
			}).render()

		// set the position as fixed because we want it to track against the mouse
		this._avatar.style('position', 'fixed')
		this._setAvatarUnderCurrentPosition()
	}

	_setAvatarUnderCurrentPosition() {
		let x = this._currentPosition[0]
		let y = this._currentPosition[1]
		let avatarNode = this._avatar.node()

		//#41258 "My first thought was to track its position relative to the upper left corner of the thing being dragged."
		//this is equal to the first cell of the avatar
		let firstCellOfAvatar = $(avatarNode).find('td')[0]

		//#40688 "We should still center it horizontally on the column."
		let width = firstCellOfAvatar.clientWidth
		let left = x - (width/2)

		//#40688 "let's keep the object being dragged in roughly the same vertical position on the column as when the drag started
		let height = firstCellOfAvatar.clientHeight
		let top = y - (height/2)

		this._avatar.style('left', `${left}px`)
			.style('top', `${top}px`)
	}

	_destroyAvatar() {
		_.invoke(this._avatar, 'remove')
		delete this._avatar
	}

	remove() {
		$('.zoneDebug').remove()
	}
}

export default TableDnd

TableDnd.__filename__ = 'reports/common/table/table-dnd.js'
